import {useEffect, useRef, useState} from "react";
import "./scss/Sidebar.scss"
import {useIntl} from "react-intl";
import {NavLink} from "react-router-dom";
import {LocaleSwitcher} from "./LocaleContext";
import {Send, Bot, Users, PersonStanding, LifeBuoy, ChartNoAxesColumn, ExternalLink, ShelvingUnit, BotMessageSquare} from 'lucide-react';
import { ReactComponent as ToggleSidebar } from "./svg/toggle.svg"
import {ReactComponent as Logo} from "./svg/logo.svg";
import useWindowDimensions from "./DataFunctions";
import {useAuthData, useCurrentUserData, useSupportBotEnabledData, useAIProviderData} from "./App";

export default function Sidebar({ collapsed = false }) {
    const [userToggleState, setUserToggleState] = useState(null)
    const [sidebarOpen, setSidebarOpen] = useState(!collapsed)
    const { width } = useWindowDimensions();
    const {supportBotEnabled} = useSupportBotEnabledData()
    const {aiProvider} = useAIProviderData()
    const isTabletOrSmaller = width < 769;

    const intl = useIntl();

    useEffect(() => {
        if (isTabletOrSmaller) {
            setSidebarOpen(false)
        } else if (userToggleState !== null) {
            // If user has manually set preference, use that
            setSidebarOpen(userToggleState)
        } else {
            // Otherwise follow the collapsed prop
            setSidebarOpen(!collapsed)
        }
    }, [width, isTabletOrSmaller])


    useEffect(() => {
        // Only update if the user hasn't manually toggled
        if (userToggleState === null) {
            setSidebarOpen(!collapsed)
        }
    }, [collapsed])

    const handleToggle = () => {
        const newState = !sidebarOpen;
        setSidebarOpen(newState);
        setUserToggleState(newState);
    }

    // Reset user toggle when going back to default views
    // This can be called from parent when appropriate
    const resetUserToggle = () => {
        setUserToggleState(null)
        setSidebarOpen(!collapsed)
    }

    const navigationItems = [
        {
            path: "/support-campaigns",
            icon: <Send size={16} strokeWidth={1.5} />,
            messageId: "supportCampaigns",
            defaultMessage: "Support Campaigns"
        },
        ...(supportBotEnabled ? [{separator: true}] : []),
        ...(supportBotEnabled ? [{
            path: "/support-bots",
            icon: <Bot size={16} strokeWidth={1.5} />,
            messageId: "supportBots",
            defaultMessage: "Support Bots"
        }] : []),
        ...(supportBotEnabled && aiProvider === "gemini" ? [{
            path: "/bot-competences",
            icon: <ShelvingUnit size={16} strokeWidth={1.5} />,
            messageId: "botCompetences",
            defaultMessage: "Bot Competences"
        }] : []),
        ...(aiProvider === "gemini" ? [{
            path: "/conversations",
            icon: <BotMessageSquare size={16} strokeWidth={1.5} />,
            messageId: "botConversations",
            defaultMessage: "Bot Conversations"
        }] : []),
        {separator: true},
        {
            path: "/contacts",
            icon: <PersonStanding size={16} strokeWidth={1.5} />,
            messageId: "main.sidebar.contacts",
            defaultMessage: "Contacts"
        },
        {
            path: "/users",
            icon: <Users size={16} strokeWidth={1.5} />,
            messageId: "main.sidebar.users",
            defaultMessage: "Users"
        }
    ];

    const externalNavItems = [
        {
            path: "/app",
            icon: <LifeBuoy size={16} strokeWidth={1.5} />,
            messageId: "app.app",
            defaultMessage: "Support Requests"
        },
        {
            path: "/stats",
            icon: <ChartNoAxesColumn size={16} strokeWidth={1.5} />,
            messageId: "app.stats",
            defaultMessage: "Stats"
        }
    ]

    const baseUrl = `${window.location.protocol}//${window.location.hostname}`

    return (
        <div className={`sidebar ${!sidebarOpen ? "closed" : ""}`}>
            <h1>
                <div className="logo">
                    {sidebarOpen && <Logo />}
                </div>

                {/* Only show toggle on desktop */}
                {!isTabletOrSmaller && (
                    <div className="toggle-sidebar" onClick={handleToggle}>
                        <ToggleSidebar />
                    </div>
                )}
            </h1>

            <nav>
                <ul>
                    {navigationItems.map((item, index) =>
                        item.separator
                            ? <li key={index} className="nav-separator" />
                            : <li key={index}>
                                <NavLink to={item.path}>
                                    {item.icon}
                                    {sidebarOpen && intl.formatMessage({
                                        id: item.messageId,
                                        defaultMessage: item.defaultMessage
                                    })}
                                </NavLink>
                            </li>
                    )}
                </ul>
            </nav>

            <nav className={"external-navigation"}>
                <ul>
                    {externalNavItems.map((item, index) => (
                        <li key={index}>
                            <a
                                href={`${baseUrl}${item.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {item.icon}
                                {sidebarOpen && (
                                    <>
                                        {intl.formatMessage({ id: item.messageId, defaultMessage: item.defaultMessage })}
                                        <ExternalLink size={12} strokeWidth={1.5} style={{ marginLeft: 'auto' }} />
                                    </>
                                )}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <Profile collapse={!sidebarOpen} />
        </div>
    );
}

function Profile({collapse}) {
    const {currentUserData} = useCurrentUserData()
    const {authData} = useAuthData()
    const firstName = currentUserData && currentUserData?.meta && currentUserData.meta.hasOwnProperty("firstname") ? currentUserData.meta.firstname : null
    const lastName = currentUserData && currentUserData?.meta && currentUserData.meta.hasOwnProperty("lastname") ? currentUserData.meta.lastname : null
    const userid = currentUserData?.meta?.userid ? currentUserData.meta.userid : (currentUserData?.meta?.email ? currentUserData.meta.email : authData.uid)
    const initials = (firstName && lastName) ? firstName.charAt(0) + lastName.charAt(0) : userid.substring(0, 2)
    const [showProfilePopover, setShowProfilePopover] = useState(false)
    const popoverRef = useRef(null)
    const popoverTriggerRef = useRef(null)
    const intl = useIntl()
    const logoutURL = authData.hasOwnProperty("logoutURL") ? authData.logoutURL : null

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                setShowProfilePopover(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])

    return <div className={collapse ? "profile-container collapse" : "profile-container"}>
        {collapse ?
            <button className={"profile-icon"} ref={popoverTriggerRef} onClick={()=>setShowProfilePopover(!showProfilePopover)}>{initials}</button> :
                <button className={"profile-button-container"} ref={popoverTriggerRef} onClick={()=>setShowProfilePopover(!showProfilePopover)}>
                    <div className={"profile-icon"}>{initials}</div>
                    {intl.formatMessage({
                        id: 'settings',
                        defaultMessage: 'Settings',
                    })}
                </button>
                }
        {showProfilePopover &&
        <div className={"profile-popover"} ref={popoverRef}>
            <>
                <h2>
                    {(firstName && lastName) ?
                        firstName +" "+ lastName :
                        userid
                    }
                </h2>
                <hr/>
            </>

            <h4>
                {intl.formatMessage({
                    id: 'profile',
                    defaultMessage: 'Profile',
                })}
            </h4>
            <p>{userid}</p>
            <hr/>
            <h4>
                {intl.formatMessage({
                    id: 'language',
                    defaultMessage: 'Language',
                })}
            </h4>
            <LocaleSwitcher />
            {logoutURL &&
            <>
                <hr/>
                <a href={logoutURL}>
                    {intl.formatMessage({
                        id: 'logOut',
                        defaultMessage: 'Log out',
                    })}
                </a>
            </>
            }

        </div>
        }
    </div>
}